import React, { useState } from 'react';
import Loading from '../../../Components/Loading';
import {
	useLazyMetaQuery,
	useLazyWorkplaceDoctorsQuery,
	useLazyWorkplaceNameQuery,
	WorkplaceMemberRole,
} from '../../../graphql/graphql';
import { SelectValue } from '../../../Components/Base/Select/Select';
import { useEffectAsync } from '../../../helpers';
import { TimelineCalendarResource } from '../../Calendar/Components/TimelineCalendar/TimelineCalendar';
import useTranslation from '../../../Hooks/useTranslation';

export interface WorkplaceMembersSelectValue extends SelectValue {
	userId: string;
}

export interface WorkingHoursDay {
	timeFrom: string;
	timeTo: string;
}

export interface WorkingHours {
	mon: WorkingHoursDay | null;
	tue: WorkingHoursDay | null;
	wed: WorkingHoursDay | null;
	thu: WorkingHoursDay | null;
	fri: WorkingHoursDay | null;
	sat: WorkingHoursDay | null;
	sun: WorkingHoursDay | null;
}

export interface ServiceScheduleContextProps {
	workplaceMembersSelectValues: WorkplaceMembersSelectValue[];
	workplaceId: string;
	timelineCalendarResources: TimelineCalendarResource[];
	workingHours: WorkingHours;
	workingHoursExtra: WorkingHours;
	workplaceName?: string;
}

export interface ScheduleServiceContextProviderProps {
	children?: React.ReactChild | React.ReactChild[];
	workplaceId: string;
}

const WORKING_HOURS_DEFAULTS: WorkingHours = {
	mon: null,
	tue: null,
	wed: null,
	thu: null,
	fri: null,
	sat: null,
	sun: null,
};

export const ServiceScheduleContext =
	React.createContext<ServiceScheduleContextProps>({
		workplaceMembersSelectValues: [],
		workplaceId: '',
		timelineCalendarResources: [],
		workingHours: WORKING_HOURS_DEFAULTS,
		workingHoursExtra: WORKING_HOURS_DEFAULTS,
		workplaceName: '',
	});

export const useServiceScheduleContext: () => ServiceScheduleContextProps =
	() => {
		return React.useContext<ServiceScheduleContextProps>(
			ServiceScheduleContext,
		);
	};

export function ServiceScheduleContextProvider({
	children,
	workplaceId,
}: ScheduleServiceContextProviderProps) {
	const [getWorkplaceDoctors] = useLazyWorkplaceDoctorsQuery();
	const [getWorkplaceName] = useLazyWorkplaceNameQuery();
	const [getMeta] = useLazyMetaQuery();
	const { t } = useTranslation();
	const [isLoading, setIsLoading] = useState(false);
	const [workplaceName, setWorkplaceName] = useState<string | undefined>(
		undefined,
	);
	const [workplaceMembersSelectValues, setWorkplaceMembersSelectValues] =
		useState<WorkplaceMembersSelectValue[]>([]);
	const [timelineCalendarResources, setTimelineCalendarResources] = useState<
		TimelineCalendarResource[]
	>([]);
	const [workingHours, setWorkingHours] = useState<WorkingHours>(
		WORKING_HOURS_DEFAULTS,
	);
	const [workingHoursExtra, setWorkingHoursExtra] = useState<WorkingHours>(
		WORKING_HOURS_DEFAULTS,
	);

	const roleTranslationMap: Record<string, string> = {
		doctor: t('component.serviceScheduleContext.roles.doctors'),
		nurse: t('component.serviceScheduleContext.roles.nurses'),
		therapist: t('component.serviceScheduleContext.roles.therapists'),
	};

	function getGroupNameFromUserRoles(roles: WorkplaceMemberRole[]): string {
		const roleKeys = roles?.map(roleObj => roleObj.role?.key) ?? [];
		const priorityOrder = ['doctor', 'nurse', 'therapist'];
		for (const key of priorityOrder) {
			if (roleKeys.includes(key)) {
				return roleTranslationMap[key];
			}
		}
		return t('component.serviceScheduleContext.roles.others');
	}

	type InputData = {
		[key in keyof WorkingHours]: { timeFrom: string; timeTo: string }[] | null;
	};

	function convertToWorkingHours(input: InputData): WorkingHours {
		const getMinMaxTime = (
			entries: { timeFrom: string; timeTo: string }[],
		): WorkingHoursDay => {
			const minTimeFrom = entries.reduce(
				(min, entry) => (entry.timeFrom < min ? entry.timeFrom : min),
				entries[0].timeFrom,
			);
			const maxTimeTo = entries.reduce(
				(max, entry) => (entry.timeTo > max ? entry.timeTo : max),
				entries[0].timeTo,
			);
			return { timeFrom: minTimeFrom, timeTo: maxTimeTo };
		};

		return Object.keys(input).reduce((acc, day) => {
			const dayKey = day as keyof WorkingHours;
			acc[dayKey] = input[dayKey] ? getMinMaxTime(input[dayKey]!) : null;
			return acc;
		}, {} as WorkingHours);
	}

	const loadWorkingHours = async () => {
		const workingHoursData = await getMeta({
			key: 'workingHours',
			workplaceId,
		});
		const workingHoursExtraData = await getMeta({
			key: 'workingHoursExtra',
			workplaceId,
		});
		if (workingHoursData?.data?.meta?.value) {
			setWorkingHours(convertToWorkingHours(workingHoursData.data.meta.value));
		}
		if (workingHoursExtraData?.data?.meta?.value) {
			setWorkingHoursExtra(
				convertToWorkingHours(workingHoursExtraData.data.meta.value),
			);
		}
	};

	useEffectAsync(async () => {
		setIsLoading(true);
		const temp: WorkplaceMembersSelectValue[] = [];
		const tempResources: TimelineCalendarResource[] = [];

		const workplaceDoctors = await getWorkplaceDoctors({
			workplaceId,
			roleKeys: ['doctor', 'nurse', 'therapist'],
			take: 999,
		});

		workplaceDoctors?.data?.workplaceDoctors?.data?.forEach(workplaceDoctor => {
			if (
				workplaceDoctor?.user?.id &&
				workplaceDoctor?.user?.profile?.fullName &&
				workplaceDoctor?.id
			)
				temp.push({
					label: workplaceDoctor.user.profile.fullName,
					value: workplaceDoctor.id,
					userId: workplaceDoctor.user.id,
				});
		});

		setWorkplaceMembersSelectValues(temp);

		workplaceDoctors?.data?.workplaceDoctors?.data?.forEach(workplaceDoctor => {
			const roleKey = getGroupNameFromUserRoles(
				workplaceDoctor?.workplaceMemberRoles as WorkplaceMemberRole[],
			);
			if (
				workplaceDoctor?.user?.profile?.fullName &&
				workplaceDoctor?.user?.id &&
				roleKey
			) {
				if (!tempResources.some(res => res.id === workplaceDoctor?.user?.id)) {
					tempResources.push({
						id: workplaceDoctor.user.id,
						title: workplaceDoctor.user.profile.fullName,
						groupKey: roleKey,
					});
				}
			}
		});

		setTimelineCalendarResources(
			tempResources.sort((a, b) =>
				a.groupKey === t('component.serviceScheduleContext.roles.doctors')
					? -1
					: 1,
			),
		);

		const workplace = await getWorkplaceName({ id: workplaceId });

		if (workplace?.data?.workplace?.name) {
			setWorkplaceName(workplace.data.workplace.name);
		}

		await loadWorkingHours();
		setIsLoading(false);
	}, []);

	return (
		<ServiceScheduleContext.Provider
			value={{
				workplaceMembersSelectValues,
				workplaceId,
				timelineCalendarResources,
				workingHours,
				workingHoursExtra,
				workplaceName,
			}}>
			{isLoading && <Loading />}
			{!isLoading && children}
		</ServiceScheduleContext.Provider>
	);
}
